import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Plus, X } from "lucide-react";

import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import { PurchaseOrderServiceSkuWhen } from "@/components/mrp/PurchaseOrderServiceSkuWhen";
import { DeferredCommitDateInput } from "@/components/mrp/DeferredCommitDateInput";
import { DeferredCommitQuantityInput } from "@/components/mrp/DeferredCommitQuantityInput";
import { DeferredCommitTextArea } from "@/components/mrp/DeferredCommitTextArea";
import { DeferredCommitUnitPriceInput } from "@/components/mrp/DeferredCommitUnitPriceInput";
import { MrpSkuCatalogHoverHintBody } from "@/components/mrp/MrpSkuCatalogHoverHintBody";
import { PurchaseOrderDraftTransferRequestFields } from "@/components/mrp/PurchaseOrderDraftTransferRequestFields";
import { PurchaseOrderDraftUnitPriceInput } from "@/components/mrp/PurchaseOrderDraftUnitPriceInput";
import { MrpSkuDetailLink } from "@/components/MrpSkuDetailLink";
import { Input } from "@/components/ui/Input";
import { HoverHintContent, HoverHintTrigger, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { cn, trimText } from "@/lib/utils";
import { formatMrpPurchaseOrderLineUnit } from "@/lib/mrpSkuUnitDisplay";
import {
  buildMrpSkuCatalogLineDescription,
  formatNumber,
  mrpPurchaseOrderLineDescriptionDiffersFromSku,
  type MrpSkuHoverHintFields,
} from "@/lib/mrp";
import {
  draftLineExtendedAmount,
  formatPurchaseOrderLineAmount,
  parsePurchaseOrderAmountRoundingMode,
  type PurchaseOrderAmountRoundingMode,
} from "@/lib/purchaseOrderAmountRounding";
import {
  collectDraftTransferRequestsForLineCluster,
  draftSkuClusterLineIndices,
  findDraftAccidentalDuplicateSkuLineIndices,
  isLastTransferPanelAnchorLine,
  isPrimaryDraftSkuClusterLine,
  primaryDraftSkuClusterLineIndex,
  purchaseOrderClusterLineQuantity,
  sumTransferRequestQuantities,
} from "@/lib/purchaseOrderLineGroups";
import {
  addCommercialSplitLineAfter,
  defaultTransferRequestForLine,
  formatDraftUnitPriceDisplay,
  prefillPurchaseOrderLineFromSkuCatalog,
  removeDraftLineAt,
  unsavedPurchaseOrderLineReadyToCommit,
  updateDraftLineAt,
  type MvPoLineForm,
  type MvPoLineTransferRequestForm,
  type PurchaseOrderCurrency,
} from "@/lib/purchaseOrderDraftForm";

const DUPLICATE_SKU_HOVER_HINT =
  "This SKU is already on another row. Use + beside the first row to split the same SKU into another commercial line, or enter a different SKU.";

const SPLIT_SKU_HOVER_HINT =
  "Add another commercial line for this SKU (for example a different unit price or description).";

const SPLIT_DELIVERY_HOVER_HINT =
  "Delivery split (D): split this line into multiple delivery dates and quantities.";

const TRANSFER_REQUEST_HOVER_HINT =
  "Transfer request (T): add planner transfer demand for this stock keeping unit. Open requests appear on the Transfers workbench after save.";

const LINE_DESCRIPTION_LOCKED_HOVER_HINT =
  "Line descriptions can be edited only while the order is Pending. Revert to pending to unlock.";

const LINE_DESCRIPTION_HUB_LOCKED_HOVER_HINT =
  "Cancelled or closed purchase orders cannot be edited.";

const LINE_UNIT_PRICE_LOCKED_HOVER_HINT =
  "Unit prices can be edited on Pending or Approved purchase orders. On Approved orders the hub reverts, saves, and re-approves in Megaventory automatically.";

const LINE_UNIT_PRICE_HUB_LOCKED_HOVER_HINT =
  "Cancelled or closed purchase orders cannot be edited.";

const LINE_QUANTITY_HUB_FLOOR_HOVER_HINT =
  "Quantity cannot be below the amount already received.";

const LINE_QUANTITY_LOCKED_HOVER_HINT =
  "Line quantities on Megaventory-owned orders can be edited only while the order is Pending. Revert to pending to unlock. Purchase invoices block revert.";

function purchaseOrderLineUnitLabel(
  line: MvPoLineForm,
  skuHoverHints: Record<string, MrpSkuHoverHintFields> | undefined,
): string {
  const sku = trimText(line.sku);
  if (sku === "") {
    return "—";
  }
  return formatMrpPurchaseOrderLineUnit(line.mv_unit ?? skuHoverHints?.[sku]?.mv_unit);
}

export type PurchaseOrderLinesTableMode = "draft" | "saved";

export interface PurchaseOrderSavedLineHandlers {
  purchaseOrderId: number;
  canCreateTransferRequest: boolean;
  /** Line descriptions on saved POs. Hub pending/approved; Megaventory pending only. */
  canEditLineDescriptions: boolean;
  /** Unit prices on saved POs; hub pending/approved, Megaventory reverts when needed. */
  canEditLineUnitPrice: boolean;
  /** Hub-owned saved POs: ordered quantity is always editable, floored at received. */
  canEditLineQuantity: boolean;
  /** Hub-owned saved POs: split some quantity onto a new commercial row. */
  canSplitCommercialLine: boolean;
  /** Hub-owned pending/approved POs: add a SKU line or remove an unreceived line. */
  canAddRemoveLines: boolean;
  /** Hover when saved remove is locked because the line has receipts. */
  lineRemoveLockedHoverHint: string;
  /** Hover when saved quantity is locked (Megaventory-owned, cancelled, or closed). */
  lineQuantityLockedHoverHint: string;
  lineDescriptionLockedHoverHint?: string;
  lineUnitPriceLockedHoverHint?: string;
  onCommitLineDescription: (purchaseOrderItemId: number, description: string | null) => Promise<void>;
  onCommitLineUnitPrice: (purchaseOrderItemId: number, unitPrice: number) => Promise<void>;
  onCommitLineQuantity: (purchaseOrderItemId: number, quantity: number) => Promise<void>;
  onSplitCommercialLine?: (purchaseOrderItemId: number, quantity: number) => Promise<void>;
  onCommitNewLine?: (line: MvPoLineForm) => Promise<void>;
  onRemoveLine?: (purchaseOrderItemId: number) => Promise<void>;
  descriptionSavePendingForItemId: (purchaseOrderItemId: number) => boolean;
  unitPriceSavePendingForItemId: (purchaseOrderItemId: number) => boolean;
  quantitySavePendingForItemId: (purchaseOrderItemId: number) => boolean;
  onOpenSplitDeliveries: (purchaseOrderItemId: number, cpn: string, prefillExpectedArrival: string | null) => void;
  onAddTransferRequest: (
    purchaseOrderItemId: number,
    sku: string,
    openQuantity: number,
    targetDate: string,
  ) => Promise<void>;
  onUpdateTransferRequest: (
    transferRequestId: string,
    patch: Partial<MvPoLineTransferRequestForm>,
  ) => Promise<void>;
  onRemoveTransferRequest: (transferRequestId: string) => Promise<void>;
}

function draftLineTotal(line: MvPoLineForm, mode: PurchaseOrderAmountRoundingMode): number | null {
  return draftLineExtendedAmount(line.quantity, line.unit_price, mode);
}

function purchaseOrderLineDescriptionDivergenceHoverHint(catalogDescription: string | null): string {
  if (catalogDescription) {
    return `Differs from SKU (${catalogDescription})`;
  }
  return "Differs from SKU catalog description.";
}

function addTransferRequestForLineCluster(
  lines: MvPoLineForm[],
  lineIndex: number,
  locationCode: string,
): MvPoLineForm[] {
  const primaryIndex = primaryDraftSkuClusterLineIndex(lines, lineIndex);
  const primaryLine = lines[primaryIndex]!;
  const existingRequests = collectDraftTransferRequestsForLineCluster(lines, primaryIndex);
  const clusterIndices = draftSkuClusterLineIndices(lines, lineIndex);
  const next = [...lines];
  for (const index of clusterIndices) {
    next[index] = { ...next[index]!, transfer_requests: [] };
  }
  next[primaryIndex] = {
    ...next[primaryIndex]!,
    transfer_requests: [
      ...existingRequests,
      defaultTransferRequestForLine(primaryLine, locationCode),
    ],
  };
  return next;
}

function PurchaseOrderDraftTransferExpandedPanel({
  lineIndex,
  lines,
  onLinesChange,
  locationCode,
  disabled,
  hubWritesAllowed,
  mode,
  savedLineHandlers,
}: {
  lineIndex: number;
  lines: MvPoLineForm[];
  onLinesChange: (next: MvPoLineForm[]) => void;
  locationCode: string;
  disabled: boolean;
  hubWritesAllowed: boolean;
  mode: PurchaseOrderLinesTableMode;
  savedLineHandlers?: PurchaseOrderSavedLineHandlers;
}): JSX.Element | null {
  const primaryIndex = primaryDraftSkuClusterLineIndex(lines, lineIndex);
  const line = lines[primaryIndex]!;
  const transferRequests = collectDraftTransferRequestsForLineCluster(lines, primaryIndex);
  if (transferRequests.length === 0) {
    return null;
  }

  const clusterIndices = draftSkuClusterLineIndices(lines, lineIndex);
  const setTransferRequests = (nextTransferRequests: MvPoLineForm["transfer_requests"]): void => {
    const next = [...lines];
    for (const index of clusterIndices) {
      next[index] = { ...next[index]!, transfer_requests: [] };
    }
    next[primaryIndex] = { ...next[primaryIndex]!, transfer_requests: nextTransferRequests };
    onLinesChange(next);
  };

  const updateTransferRequest = (
    clientId: string,
    patch: Partial<MvPoLineForm["transfer_requests"][number]>,
  ): void => {
    setTransferRequests(
      transferRequests.map((transferRequest) =>
        transferRequest.client_id === clientId ? { ...transferRequest, ...patch } : transferRequest,
      ),
    );
  };

  const removeTransferRequest = (clientId: string): void => {
    setTransferRequests(transferRequests.filter((transferRequest) => transferRequest.client_id !== clientId));
  };

  const lineOrderQuantity = purchaseOrderClusterLineQuantity(lines, primaryIndex);
  const totalTransferQuantity = sumTransferRequestQuantities(transferRequests);
  const transferQuantityExceedsLine = totalTransferQuantity > lineOrderQuantity;

  return (
    <tr className="bg-slate-50">
      <td colSpan={10} className="px-3 py-3">
        <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
          {transferQuantityExceedsLine ? (
            <p className="text-sm text-rose-700">
              Transfer quantities total{" "}
              <span className="font-mono tabular-nums">{formatNumber(totalTransferQuantity)}</span> but this line
              orders{" "}
              <span className="font-mono tabular-nums">{formatNumber(lineOrderQuantity)}</span>. Reduce transfer
              demand by{" "}
              <span className="font-mono tabular-nums">
                {formatNumber(totalTransferQuantity - lineOrderQuantity)}
              </span>
              .
            </p>
          ) : null}
          {transferRequests.map((transferRequest, transferRequestIndex) => (
            <PurchaseOrderDraftTransferRequestFields
              key={transferRequest.client_id}
              transferRequest={transferRequest}
              transferRequestIndex={transferRequestIndex}
              lineSku={line.sku}
              locationCode={locationCode}
              disabled={disabled}
              hubWritesAllowed={hubWritesAllowed}
              quantityHasError={transferQuantityExceedsLine}
              onUpdate={(patch) => {
                if (mode === "saved" && savedLineHandlers != null) {
                  void savedLineHandlers.onUpdateTransferRequest(transferRequest.client_id, patch);
                  return;
                }
                updateTransferRequest(transferRequest.client_id, patch);
              }}
              onRemove={() => {
                if (mode === "saved" && savedLineHandlers != null) {
                  void savedLineHandlers.onRemoveTransferRequest(transferRequest.client_id);
                  return;
                }
                removeTransferRequest(transferRequest.client_id);
              }}
            />
          ))}
        </div>
      </td>
    </tr>
  );
}

function PurchaseOrderDraftCommercialLineRow({
  line,
  lineIndex,
  lines,
  onLinesChange,
  locationCode,
  currency,
  amountRoundingMode,
  disabled,
  hubWritesAllowed,
  skuHoverHints,
  canRemove,
  onFieldBlur,
  isDuplicateSku,
  isSplitChild,
  showSplitButton,
  showTransferButton,
  showTransferPanel,
  mode,
  savedLineHandlers,
}: {
  line: MvPoLineForm;
  lineIndex: number;
  lines: MvPoLineForm[];
  onLinesChange: (next: MvPoLineForm[]) => void;
  locationCode: string;
  currency: PurchaseOrderCurrency | string | null;
  amountRoundingMode: PurchaseOrderAmountRoundingMode;
  disabled: boolean;
  hubWritesAllowed: boolean;
  skuHoverHints: Record<string, MrpSkuHoverHintFields> | undefined;
  canRemove: boolean;
  onFieldBlur?: (linesOverride?: MvPoLineForm[]) => void;
  isDuplicateSku: boolean;
  isSplitChild: boolean;
  showSplitButton: boolean;
  showTransferButton: boolean;
  showTransferPanel: boolean;
  mode: PurchaseOrderLinesTableMode;
  savedLineHandlers?: PurchaseOrderSavedLineHandlers;
}): JSX.Element {
  const queryClient = useQueryClient();
  const isSavedMode = mode === "saved";
  const isDeliverySegment = line.is_delivery_segment === true;
  const isUnsavedNewLine = isSavedMode && line.purchase_order_item_id == null && !isDeliverySegment;
  const catalogDescription =
    line.sku_catalog_description?.trim() ||
    buildMrpSkuCatalogLineDescription(skuHoverHints?.[trimText(line.sku)]);
  const descriptionDiffersFromSku = mrpPurchaseOrderLineDescriptionDiffersFromSku(
    line.line_description,
    catalogDescription,
  );
  const lineTotal = draftLineTotal(line, amountRoundingMode);
  const [expectedDateJustSaved, setExpectedDateJustSaved] = useState(false);
  const [descriptionJustSaved, setDescriptionJustSaved] = useState(false);
  const [descriptionSaveError, setDescriptionSaveError] = useState<string | null>(null);
  const [unitPriceJustSaved, setUnitPriceJustSaved] = useState(false);
  const [unitPriceSaveError, setUnitPriceSaveError] = useState<string | null>(null);
  const [quantityJustSaved, setQuantityJustSaved] = useState(false);
  const [quantitySaveError, setQuantitySaveError] = useState<string | null>(null);
  const [splitSaveError, setSplitSaveError] = useState<string | null>(null);
  const [splitSavePending, setSplitSavePending] = useState(false);
  const [newLineSaveError, setNewLineSaveError] = useState<string | null>(null);
  const [newLineSavePending, setNewLineSavePending] = useState(false);

  useEffect(() => {
    if (!expectedDateJustSaved) return;
    const timer = window.setTimeout(() => setExpectedDateJustSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [expectedDateJustSaved]);

  useEffect(() => {
    if (!descriptionJustSaved) return;
    const timer = window.setTimeout(() => setDescriptionJustSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [descriptionJustSaved]);

  useEffect(() => {
    if (!unitPriceJustSaved) return;
    const timer = window.setTimeout(() => setUnitPriceJustSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [unitPriceJustSaved]);

  useEffect(() => {
    if (!quantityJustSaved) return;
    const timer = window.setTimeout(() => setQuantityJustSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [quantityJustSaved]);

  const patchLineExpectedDateMutation = useMutation({
    mutationFn: (expected_date: string | null) =>
      apiFetch(`/api/mrp/mv-purchase-order-items/${line.purchase_order_item_id}`, {
        method: "PATCH",
        body: { expected_date },
      }),
    onSuccess: () => {
      setExpectedDateJustSaved(true);
      void queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-orders"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-orders", "one"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "incoming"] });
    },
  });

  const patchSegmentExpectedDateMutation = useMutation({
    mutationFn: (expected_arrival: string | null) =>
      apiFetch(
        `/api/mrp/mv-purchase-order-items/${line.purchase_order_item_id}/deliveries/${line.delivery_segment_id}`,
        {
          method: "PATCH",
          body: { expected_arrival },
        },
      ),
    onSuccess: () => {
      setExpectedDateJustSaved(true);
      void queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-orders"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-orders", "one"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "po-delivery-plan", line.purchase_order_item_id] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "incoming"] });
    },
  });

  const updateLine = (patch: Partial<MvPoLineForm>): void => {
    onLinesChange(updateDraftLineAt(lines, lineIndex, patch));
  };

  const tryCommitUnsavedNewLine = (nextLines: MvPoLineForm[]): void => {
    if (!isUnsavedNewLine || savedLineHandlers?.onCommitNewLine == null || newLineSavePending) {
      return;
    }
    const committed = nextLines[lineIndex];
    if (committed == null || !unsavedPurchaseOrderLineReadyToCommit(committed)) {
      return;
    }
    setNewLineSaveError(null);
    setNewLineSavePending(true);
    void savedLineHandlers
      .onCommitNewLine(committed)
      .catch((error: unknown) => {
        setNewLineSaveError(error instanceof Error ? error.message : "Could not add this line.");
      })
      .finally(() => {
        setNewLineSavePending(false);
      });
  };

  const descriptionSavePending =
    isSavedMode &&
    !isDeliverySegment &&
    line.purchase_order_item_id != null &&
    savedLineHandlers != null &&
    savedLineHandlers.descriptionSavePendingForItemId(line.purchase_order_item_id);

  const receivedDisplay = isSavedMode
    ? formatNumber(line.quantity_received ?? 0)
    : "0";

  const vendorInvoicedDisplay = isSavedMode
    ? formatNumber(line.quantity_invoiced_total ?? 0)
    : "0";

  const gipiDisplay = isSavedMode
    ? formatNumber(line.quantity_invoiced ?? 0)
    : "0";

  const savedDescriptionEditable =
    isSavedMode &&
    !isUnsavedNewLine &&
    !isDeliverySegment &&
    savedLineHandlers != null &&
    savedLineHandlers.canEditLineDescriptions;
  const savedDescriptionReadOnly = isSavedMode && !isUnsavedNewLine && !savedDescriptionEditable;
  const unitPriceSavePending =
    isSavedMode &&
    !isDeliverySegment &&
    line.purchase_order_item_id != null &&
    savedLineHandlers != null &&
    savedLineHandlers.unitPriceSavePendingForItemId(line.purchase_order_item_id);
  const savedUnitPriceEditable =
    isSavedMode &&
    !isUnsavedNewLine &&
    !isDeliverySegment &&
    savedLineHandlers != null &&
    savedLineHandlers.canEditLineUnitPrice;
  const savedUnitPriceReadOnly = isSavedMode && !isUnsavedNewLine && !savedUnitPriceEditable;
  const quantitySavePending =
    isSavedMode &&
    !isDeliverySegment &&
    line.delivery_segment_id == null &&
    line.purchase_order_item_id != null &&
    savedLineHandlers != null &&
    savedLineHandlers.quantitySavePendingForItemId(line.purchase_order_item_id);
  const savedQuantityEditable =
    isSavedMode &&
    !isUnsavedNewLine &&
    !isDeliverySegment &&
    line.delivery_segment_id == null &&
    savedLineHandlers != null &&
    savedLineHandlers.canEditLineQuantity;
  const savedQuantityReadOnly = isSavedMode && !isUnsavedNewLine && !savedQuantityEditable;
  const receivedQuantity = line.quantity_received ?? 0;
  const orderedQuantity = Number.parseFloat(line.quantity);
  const remainingAfterDefaultSplit = orderedQuantity - 1;
  const canSplitThisSavedLine =
    isSavedMode &&
    !isDeliverySegment &&
    savedLineHandlers?.canSplitCommercialLine === true &&
    savedLineHandlers.onSplitCommercialLine != null &&
    line.purchase_order_item_id != null &&
    Number.isFinite(orderedQuantity) &&
    remainingAfterDefaultSplit > 0 &&
    remainingAfterDefaultSplit + 1e-9 >= receivedQuantity;
  const splitCommercialLockedHoverHint =
    savedLineHandlers?.canSplitCommercialLine !== true
      ? "Line splits are available on Hub-owned pending or approved purchase orders."
      : "Cannot split this line: keep at least the received quantity on the original row, and keep some quantity on that row.";
  const lineDescriptionClassName = cn(
    "min-w-0 w-full min-h-8 overflow-auto rounded-md border border-slate-300 px-3 py-1.5 text-xs font-sans text-slate-900",
    savedDescriptionReadOnly ? "cursor-default resize-none bg-slate-100" : "resize-y bg-white focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
    descriptionDiffersFromSku &&
      (savedDescriptionReadOnly
        ? "border-amber-400 bg-amber-50"
        : "border-amber-400 bg-amber-50 focus:border-amber-500 focus:ring-amber-500"),
  );

  return (
    <>
      <tr className={isSplitChild ? "bg-slate-50/40" : undefined}>
        <td className={cn("px-3 py-2 align-top", isSplitChild && "pl-8")}>
          {isSplitChild ? (
            <span className="font-mono text-xs text-slate-500">
              <span className="text-slate-400" aria-hidden>
                ↳{" "}
              </span>
              {trimText(line.sku) || "—"}
            </span>
          ) : isSavedMode && !isUnsavedNewLine ? (
            <>
            <div className="flex min-w-0 items-start gap-1">
              <HoverHintContent
                hoverHint={<MrpSkuCatalogHoverHintBody sku={line.sku} hint={skuHoverHints?.[line.sku]} />}
                block
              >
                <MrpSkuDetailLink sku={line.sku} className="font-mono text-xs text-brand-700 hover:text-brand-900 hover:underline">
                  {line.sku}
                </MrpSkuDetailLink>
              </HoverHintContent>
              {canSplitThisSavedLine || savedLineHandlers?.canSplitCommercialLine === true ? (
                <ReadOnlyHoverLayer
                  active={!canSplitThisSavedLine || !hubWritesAllowed || splitSavePending}
                  hoverHint={
                    !hubWritesAllowed
                      ? "Your role has read-only access to the MRP hub module."
                      : canSplitThisSavedLine
                        ? SPLIT_SKU_HOVER_HINT
                        : splitCommercialLockedHoverHint
                  }
                  layout="inline"
                >
                  <button
                    type="button"
                    disabled={disabled || !hubWritesAllowed || !canSplitThisSavedLine || splitSavePending}
                    onClick={() => {
                      if (
                        line.purchase_order_item_id == null ||
                        savedLineHandlers?.onSplitCommercialLine == null ||
                        splitSavePending
                      ) {
                        return;
                      }
                      setSplitSaveError(null);
                      setSplitSavePending(true);
                      void savedLineHandlers
                        .onSplitCommercialLine(line.purchase_order_item_id, 1)
                        .catch((error: unknown) => {
                          setSplitSaveError(
                            error instanceof Error ? error.message : "Could not split this line.",
                          );
                        })
                        .finally(() => setSplitSavePending(false));
                    }}
                    className="inline-flex shrink-0 rounded p-1 text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label={SPLIT_SKU_HOVER_HINT}
                  >
                    <Plus className="h-4 w-4" aria-hidden />
                  </button>
                </ReadOnlyHoverLayer>
              ) : null}
              {showSplitButton ? (
                <ReadOnlyHoverLayer
                  active={!hubWritesAllowed}
                  hoverHint={
                    !hubWritesAllowed
                      ? "Your role has read-only access to the MRP hub module."
                      : SPLIT_DELIVERY_HOVER_HINT
                  }
                  layout="inline"
                >
                  <HoverHintContent hoverHint={SPLIT_DELIVERY_HOVER_HINT}>
                    <button
                      type="button"
                      disabled={disabled || !hubWritesAllowed || trimText(line.sku) === ""}
                      onClick={() => {
                        if (line.purchase_order_item_id == null || savedLineHandlers == null) return;
                        savedLineHandlers.onOpenSplitDeliveries(
                          line.purchase_order_item_id,
                          line.sku,
                          trimText(line.expected_delivery_date) !== "" ? trimText(line.expected_delivery_date) : null,
                        );
                      }}
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded p-0 font-mono text-xs font-bold leading-none text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label={SPLIT_DELIVERY_HOVER_HINT}
                    >
                      D
                    </button>
                  </HoverHintContent>
                </ReadOnlyHoverLayer>
              ) : null}
              {showTransferButton ? (
                <ReadOnlyHoverLayer
                  active={!hubWritesAllowed || !savedLineHandlers?.canCreateTransferRequest}
                  hoverHint={
                    !hubWritesAllowed
                      ? "Your role has read-only access to the MRP hub module."
                      : !savedLineHandlers?.canCreateTransferRequest
                        ? "Cancelled or closed purchase orders cannot add transfer requests."
                        : TRANSFER_REQUEST_HOVER_HINT
                  }
                  layout="inline"
                >
                  <HoverHintContent hoverHint={TRANSFER_REQUEST_HOVER_HINT}>
                    <button
                      type="button"
                      disabled={
                        disabled ||
                        !hubWritesAllowed ||
                        !savedLineHandlers?.canCreateTransferRequest ||
                        trimText(line.sku) === ""
                      }
                      onClick={() => {
                        if (savedLineHandlers == null || line.purchase_order_item_id == null) return;
                        const openQuantity = Number.parseFloat(line.quantity) - (line.quantity_received ?? 0);
                        void savedLineHandlers.onAddTransferRequest(
                          line.purchase_order_item_id,
                          line.sku,
                          Number.isFinite(openQuantity) ? openQuantity : Number.parseFloat(line.quantity) || 1,
                          line.expected_delivery_date,
                        );
                      }}
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded p-0 font-mono text-xs font-bold leading-none text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label={TRANSFER_REQUEST_HOVER_HINT}
                    >
                      T
                    </button>
                  </HoverHintContent>
                </ReadOnlyHoverLayer>
              ) : null}
            </div>
            <PurchaseOrderServiceSkuWhen sku={line.sku} />
            {splitSaveError != null ? (
              <p className="mt-1 text-xs text-rose-700">{splitSaveError}</p>
            ) : null}
          </>
          ) : (
            <>
            <div className="flex min-w-0 items-start gap-1">
              <ReadOnlyHoverLayer
                active={!hubWritesAllowed}
                hoverHint="Your role has read-only access to the MRP hub module."
                layout="block"
              >
                <AssemblySkuCombobox
                  value={line.sku}
                  onChange={(next) => updateLine({ sku: next })}
              onBlurCommittedWithSku={(trimmed) => {
                void (async () => {
                  const prefilled = await prefillPurchaseOrderLineFromSkuCatalog(trimmed, {
                    line_description: line.line_description,
                    unit_price: line.unit_price,
                  });
                  const nextLines = updateDraftLineAt(lines, lineIndex, {
                    sku: trimmed,
                    line_description: prefilled.line_description,
                    ...(prefilled.unit_price != null ? { unit_price: prefilled.unit_price } : {}),
                    ...(prefilled.mv_unit !== undefined ? { mv_unit: prefilled.mv_unit } : {}),
                  });
                  onLinesChange(nextLines);
                  onFieldBlur?.(nextLines);
                  tryCommitUnsavedNewLine(nextLines);
                })();
              }}
                  enforceCatalogSku
                  disabled={disabled || !hubWritesAllowed || newLineSavePending}
                  placeholder="Search SKU…"
                  className={cn(
                    "font-mono text-xs",
                    isDuplicateSku &&
                      "border-rose-500 bg-rose-50 focus:border-rose-600 focus:ring-rose-500",
                  )}
                />
              </ReadOnlyHoverLayer>
              {isDuplicateSku ? (
                <HoverHintTrigger
                  className="shrink-0 pt-2"
                  trigger={
                    <button
                      type="button"
                      tabIndex={-1}
                      className="inline-flex shrink-0 rounded p-0.5 text-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-1"
                      aria-label={DUPLICATE_SKU_HOVER_HINT}
                    >
                      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                    </button>
                  }
                >
                  <span className="block break-words">{DUPLICATE_SKU_HOVER_HINT}</span>
                </HoverHintTrigger>
              ) : null}
              {showSplitButton ? (
                <ReadOnlyHoverLayer
                  active={!hubWritesAllowed}
                  hoverHint={SPLIT_SKU_HOVER_HINT}
                  layout="inline"
                >
                  <button
                    type="button"
                    disabled={disabled || !hubWritesAllowed || trimText(line.sku) === ""}
                    onClick={() => onLinesChange(addCommercialSplitLineAfter(lines, lineIndex, locationCode))}
                    className="inline-flex shrink-0 rounded p-1 text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label={SPLIT_SKU_HOVER_HINT}
                  >
                    <Plus className="h-4 w-4" aria-hidden />
                  </button>
                </ReadOnlyHoverLayer>
              ) : null}
              {showTransferButton ? (
                <ReadOnlyHoverLayer
                  active={!hubWritesAllowed}
                  hoverHint={
                    !hubWritesAllowed
                      ? "Your role has read-only access to the MRP hub module."
                      : TRANSFER_REQUEST_HOVER_HINT
                  }
                  layout="inline"
                >
                  <HoverHintContent hoverHint={TRANSFER_REQUEST_HOVER_HINT}>
                    <button
                      type="button"
                      disabled={disabled || !hubWritesAllowed || trimText(line.sku) === ""}
                      onClick={() =>
                        onLinesChange(addTransferRequestForLineCluster(lines, lineIndex, locationCode))
                      }
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded p-0 font-mono text-xs font-bold leading-none text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label={TRANSFER_REQUEST_HOVER_HINT}
                    >
                      T
                    </button>
                  </HoverHintContent>
                </ReadOnlyHoverLayer>
              ) : null}
              {newLineSaveError != null ? (
                <p className="mt-1 text-xs text-rose-700">{newLineSaveError}</p>
              ) : null}
            </div>
            <PurchaseOrderServiceSkuWhen sku={line.sku} />
            </>
          )}
        </td>
        <td className="min-w-[14rem] px-3 py-2 align-top">
          <div className="flex min-w-0 items-start gap-1">
            <ReadOnlyHoverLayer
              active={savedDescriptionReadOnly || !hubWritesAllowed}
              hoverHint={
                savedDescriptionReadOnly
                  ? (savedLineHandlers?.lineDescriptionLockedHoverHint ??
                    (savedLineHandlers?.canSplitCommercialLine === true
                      ? LINE_DESCRIPTION_HUB_LOCKED_HOVER_HINT
                      : LINE_DESCRIPTION_LOCKED_HOVER_HINT))
                  : "Your role has read-only access to the MRP hub module."
              }
              layout="block"
              className="min-w-0 flex-1"
            >
              {savedDescriptionEditable ? (
                <DeferredCommitTextArea
                  value={line.line_description}
                  disabled={!hubWritesAllowed}
                  pending={descriptionSavePending === true}
                  commitSucceeded={descriptionJustSaved}
                  commitError={descriptionSaveError}
                  onCommit={(description) => {
                    if (line.purchase_order_item_id == null || descriptionSavePending) return;
                    setDescriptionSaveError(null);
                    void savedLineHandlers!
                      .onCommitLineDescription(line.purchase_order_item_id, description)
                      .then(() => setDescriptionJustSaved(true))
                      .catch((error: unknown) => {
                        setDescriptionSaveError(
                          error instanceof Error ? error.message : "Could not save description.",
                        );
                      });
                  }}
                  className={cn(
                    lineDescriptionClassName,
                    descriptionDiffersFromSku &&
                      !descriptionSavePending &&
                      "border-amber-400 bg-amber-50 focus:border-amber-500 focus:ring-amber-500",
                  )}
                />
              ) : (
                <textarea
                  value={line.line_description}
                  readOnly={savedDescriptionReadOnly}
                  disabled={disabled || !hubWritesAllowed || (isSavedMode && isDeliverySegment)}
                  rows={2}
                  onChange={(event) => updateLine({ line_description: event.target.value })}
                  onBlur={() => {
                    onFieldBlur?.();
                    tryCommitUnsavedNewLine(lines);
                  }}
                  className={cn(
                    lineDescriptionClassName,
                    !isSavedMode && "placeholder:text-slate-400",
                  )}
                />
              )}
            </ReadOnlyHoverLayer>
            {descriptionDiffersFromSku ? (
              <HoverHintTrigger
                className="shrink-0 pt-2"
                trigger={
                  <button
                    type="button"
                    tabIndex={-1}
                    className="inline-flex shrink-0 rounded p-0.5 text-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                    aria-label={purchaseOrderLineDescriptionDivergenceHoverHint(catalogDescription)}
                  >
                    <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                  </button>
                }
              >
                <span className="block break-words">
                  {purchaseOrderLineDescriptionDivergenceHoverHint(catalogDescription)}
                </span>
              </HoverHintTrigger>
            ) : null}
          </div>
        </td>
        <td className="w-[4.5rem] max-w-[4.5rem] px-2 py-2 align-top">
          <span
            className="block truncate text-xs text-slate-700"
            title={purchaseOrderLineUnitLabel(line, skuHoverHints)}
          >
            {purchaseOrderLineUnitLabel(line, skuHoverHints)}
          </span>
        </td>
        <td className="w-[5.5rem] max-w-[5.5rem] px-2 py-2 align-top">
          <ReadOnlyHoverLayer
            active={savedQuantityReadOnly || !hubWritesAllowed}
            hoverHint={
              line.delivery_segment_id != null
                ? "Use delivery split (D) to change quantities on split lines."
                : savedQuantityReadOnly
                  ? (savedLineHandlers?.lineQuantityLockedHoverHint ?? LINE_QUANTITY_LOCKED_HOVER_HINT)
                  : !hubWritesAllowed
                    ? "Your role has read-only access to the MRP hub module."
                    : LINE_QUANTITY_HUB_FLOOR_HOVER_HINT
            }
            layout="block"
          >
            {isSavedMode && !isUnsavedNewLine ? (
              savedQuantityEditable ? (
                <HoverHintContent hoverHint={LINE_QUANTITY_HUB_FLOOR_HOVER_HINT} block>
                  <DeferredCommitQuantityInput
                    value={line.quantity}
                    disabled={!hubWritesAllowed}
                    pending={quantitySavePending === true}
                    commitSucceeded={quantityJustSaved}
                    commitError={quantitySaveError}
                    minimumQuantity={receivedQuantity}
                    onCommit={(quantity) => {
                      if (line.purchase_order_item_id == null || quantitySavePending) return;
                      setQuantitySaveError(null);
                      void savedLineHandlers!
                        .onCommitLineQuantity(line.purchase_order_item_id, quantity)
                        .then(() => setQuantityJustSaved(true))
                        .catch((error: unknown) => {
                          setQuantitySaveError(
                            error instanceof Error ? error.message : "Could not save quantity.",
                          );
                        });
                    }}
                    className="h-8 w-full max-w-[5rem] text-right font-mono text-xs"
                  />
                </HoverHintContent>
              ) : (
                <Input
                  readOnly
                  value={formatNumber(Number.parseFloat(line.quantity) || 0)}
                  className="h-8 w-full max-w-[5rem] cursor-default bg-slate-100 text-right font-mono text-xs text-slate-700"
                  aria-readonly="true"
                />
              )
            ) : (
              <Input
                type="number"
                min={0}
                step="any"
                value={line.quantity}
                disabled={disabled || !hubWritesAllowed}
                onChange={(event) => updateLine({ quantity: event.target.value })}
                onBlur={() => {
                  onFieldBlur?.();
                  tryCommitUnsavedNewLine(lines);
                }}
                className="h-8 w-full max-w-[5rem] text-right font-mono text-xs"
              />
            )}
          </ReadOnlyHoverLayer>
        </td>
        <td className="w-[9.5rem] max-w-[9.5rem] px-2 py-2 align-top">
          <ReadOnlyHoverLayer
            active={savedUnitPriceReadOnly || !hubWritesAllowed}
            hoverHint={
              savedUnitPriceReadOnly
                ? (savedLineHandlers?.lineUnitPriceLockedHoverHint ??
                  (savedLineHandlers?.canSplitCommercialLine === true
                    ? LINE_UNIT_PRICE_HUB_LOCKED_HOVER_HINT
                    : LINE_UNIT_PRICE_LOCKED_HOVER_HINT))
                : "Your role has read-only access to the MRP hub module."
            }
            layout="block"
          >
            {isSavedMode && !isUnsavedNewLine ? (
              savedUnitPriceEditable ? (
                <DeferredCommitUnitPriceInput
                  value={line.unit_price}
                  disabled={!hubWritesAllowed}
                  pending={unitPriceSavePending === true}
                  commitSucceeded={unitPriceJustSaved}
                  commitError={unitPriceSaveError}
                  onCommit={(unitPrice) => {
                    if (line.purchase_order_item_id == null || unitPriceSavePending) return;
                    setUnitPriceSaveError(null);
                    void savedLineHandlers!
                      .onCommitLineUnitPrice(line.purchase_order_item_id, unitPrice)
                      .then(() => setUnitPriceJustSaved(true))
                      .catch((error: unknown) => {
                        setUnitPriceSaveError(
                          error instanceof Error ? error.message : "Could not save unit price.",
                        );
                      });
                  }}
                  className="h-8 w-full max-w-[9rem] text-right font-mono text-xs"
                />
              ) : (
                <Input
                  readOnly
                  value={
                    trimText(line.unit_price) === ""
                      ? "—"
                      : formatDraftUnitPriceDisplay(line.unit_price)
                  }
                  className="h-8 w-full max-w-[9rem] cursor-default bg-slate-100 text-right font-mono text-xs text-slate-700"
                  aria-readonly="true"
                />
              )
            ) : (
              <PurchaseOrderDraftUnitPriceInput
                value={line.unit_price}
                disabled={disabled || !hubWritesAllowed}
                onChange={(next) => updateLine({ unit_price: next })}
                onBlur={() => {
                  const formatted = formatDraftUnitPriceDisplay(line.unit_price);
                  const nextLines = updateDraftLineAt(lines, lineIndex, { unit_price: formatted });
                  onLinesChange(nextLines);
                  onFieldBlur?.(nextLines);
                  tryCommitUnsavedNewLine(nextLines);
                }}
                className="h-8 w-full max-w-[9rem] text-right font-mono text-xs"
              />
            )}
          </ReadOnlyHoverLayer>
        </td>
        <td className="px-3 py-2 text-right font-mono text-xs align-top">
          {formatPurchaseOrderLineAmount(lineTotal, currency, amountRoundingMode)}
        </td>
        <td className="px-3 py-2 text-right font-mono text-xs align-top tabular-nums">{receivedDisplay}</td>
        <td className="px-3 py-2 text-right font-mono text-xs align-top tabular-nums">{vendorInvoicedDisplay}</td>
        <td className="px-3 py-2 text-right font-mono text-xs align-top tabular-nums">{gipiDisplay}</td>
        <td className="whitespace-nowrap px-3 py-2 align-top">
          {isSavedMode && !isUnsavedNewLine ? (
            <ReadOnlyHoverLayer
              active={!hubWritesAllowed}
              hoverHint="Your role has read-only access to the MRP hub module."
              layout="inline"
            >
              {line.delivery_segment_id != null ? (
                <DeferredCommitDateInput
                  value={line.expected_delivery_date}
                  disabled={!hubWritesAllowed}
                  pending={patchSegmentExpectedDateMutation.isPending}
                  commitSucceeded={expectedDateJustSaved}
                  commitError={
                    patchSegmentExpectedDateMutation.error != null
                      ? (patchSegmentExpectedDateMutation.error as Error).message
                      : null
                  }
                  onCommit={(expected_arrival) => {
                    patchSegmentExpectedDateMutation.mutate(expected_arrival);
                  }}
                  className="h-8 w-[9.5rem] text-xs"
                  statusGlyphClassName="pt-1.5"
                />
              ) : (
                <DeferredCommitDateInput
                  value={line.expected_delivery_date}
                  disabled={!hubWritesAllowed}
                  pending={patchLineExpectedDateMutation.isPending}
                  commitSucceeded={expectedDateJustSaved}
                  commitError={
                    patchLineExpectedDateMutation.error != null
                      ? (patchLineExpectedDateMutation.error as Error).message
                      : null
                  }
                  onCommit={(expected_date) => {
                    patchLineExpectedDateMutation.mutate(expected_date);
                  }}
                  className="h-8 w-[9.5rem] text-xs"
                  statusGlyphClassName="pt-1.5"
                />
              )}
            </ReadOnlyHoverLayer>
          ) : (
            <ReadOnlyHoverLayer
              active={!hubWritesAllowed}
              hoverHint="Your role has read-only access to the MRP hub module."
              layout="inline"
            >
              <Input
                type="date"
                value={line.expected_delivery_date}
                disabled={disabled || !hubWritesAllowed}
                onChange={(event) => updateLine({ expected_delivery_date: event.target.value })}
                onBlur={() => {
                  onFieldBlur?.();
                  tryCommitUnsavedNewLine(lines);
                }}
                className="h-8 w-[9.5rem] text-xs"
              />
            </ReadOnlyHoverLayer>
          )}
        </td>
        <td className="min-w-[11rem] px-2 py-2 text-right align-top">
          {canRemove && !isDeliverySegment ? (
            <ReadOnlyHoverLayer
              active={
                !hubWritesAllowed ||
                (isSavedMode && !isUnsavedNewLine && (line.quantity_received ?? 0) > 0)
              }
              hoverHint={
                !hubWritesAllowed
                  ? "Your role has read-only access to the MRP hub module."
                  : isSavedMode && !isUnsavedNewLine && (line.quantity_received ?? 0) > 0
                    ? (savedLineHandlers?.lineRemoveLockedHoverHint ??
                      "Cannot remove a line that has already been received.")
                    : "Remove this commercial line."
              }
              layout="inline"
            >
              <button
                type="button"
                disabled={
                  disabled ||
                  !hubWritesAllowed ||
                  newLineSavePending ||
                  (isSavedMode && !isUnsavedNewLine && (line.quantity_received ?? 0) > 0)
                }
                onClick={() => {
                  if (isUnsavedNewLine || !isSavedMode) {
                    onLinesChange(removeDraftLineAt(lines, lineIndex));
                    return;
                  }
                  if (line.purchase_order_item_id == null || savedLineHandlers?.onRemoveLine == null) {
                    return;
                  }
                  void savedLineHandlers.onRemoveLine(line.purchase_order_item_id);
                }}
                className="inline-flex rounded p-1 text-rose-600 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Remove line"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </ReadOnlyHoverLayer>
          ) : null}
        </td>
      </tr>
      {showTransferPanel ? (
        <PurchaseOrderDraftTransferExpandedPanel
          lineIndex={lineIndex}
          lines={lines}
          onLinesChange={onLinesChange}
          locationCode={locationCode}
          disabled={disabled}
          hubWritesAllowed={hubWritesAllowed}
          mode={mode}
          savedLineHandlers={savedLineHandlers}
        />
      ) : null}
    </>
  );
}

export function PurchaseOrderGroupedDraftLines({
  lines,
  onLinesChange,
  locationCode,
  currency,
  amountRoundingMode = "line",
  disabled,
  hubWritesAllowed,
  skuHoverHints,
  onFieldBlur,
  mode = "draft",
  savedLineHandlers,
}: {
  lines: MvPoLineForm[];
  onLinesChange: (next: MvPoLineForm[]) => void;
  locationCode: string;
  currency: PurchaseOrderCurrency | string | null;
  amountRoundingMode?: PurchaseOrderAmountRoundingMode;
  disabled: boolean;
  hubWritesAllowed: boolean;
  skuHoverHints: Record<string, MrpSkuHoverHintFields> | undefined;
  onFieldBlur?: (linesOverride?: MvPoLineForm[]) => void;
  mode?: PurchaseOrderLinesTableMode;
  savedLineHandlers?: PurchaseOrderSavedLineHandlers;
}): JSX.Element {
  const duplicateSkuLineIndices =
    mode === "draft" ? findDraftAccidentalDuplicateSkuLineIndices(lines) : new Set<number>();

  return (
    <>
      {lines.map((line, lineIndex) => {
        const isDeliverySegment = line.is_delivery_segment === true;
        const isPrimaryClusterLine = isPrimaryDraftSkuClusterLine(lines, lineIndex) && !isDeliverySegment;
        const isLastPanelAnchor = isLastTransferPanelAnchorLine(lines, lineIndex);
        const isSplitChild = isDeliverySegment || (line.sku_split_group_id != null && !isPrimaryClusterLine);

        return (
          <PurchaseOrderDraftCommercialLineRow
            key={line.client_id}
            line={line}
            lineIndex={lineIndex}
            lines={lines}
            onLinesChange={onLinesChange}
            locationCode={locationCode}
            currency={currency}
            amountRoundingMode={parsePurchaseOrderAmountRoundingMode(amountRoundingMode)}
            disabled={disabled}
            hubWritesAllowed={hubWritesAllowed}
            skuHoverHints={skuHoverHints}
            canRemove={
              mode === "draft"
                ? lines.length > 1
                : savedLineHandlers?.canAddRemoveLines === true
            }
            onFieldBlur={onFieldBlur}
            isDuplicateSku={duplicateSkuLineIndices.has(lineIndex)}
            isSplitChild={isSplitChild}
            showSplitButton={
              isPrimaryClusterLine && !(mode === "saved" && line.purchase_order_item_id == null)
            }
            showTransferButton={
              isPrimaryClusterLine && !(mode === "saved" && line.purchase_order_item_id == null)
            }
            showTransferPanel={isLastPanelAnchor}
            mode={mode}
            savedLineHandlers={savedLineHandlers}
          />
        );
      })}
    </>
  );
}

/** @deprecated Use PurchaseOrderGroupedDraftLines with mode="saved" instead. */
export const PurchaseOrderGroupedSavedLines = PurchaseOrderGroupedDraftLines;
